import {
  useWSSnackbar,
  WSActions,
  WSForm,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSLoader
} from "@wingspanhq/fe-component-library";
import { useRequirementDefinitionQuery } from "../../../../query/requirementDefinitions/query/useRequirementDefinitionQuery";
import { WSQueries } from "../../../../query/WSQuery";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { ErrorContextKey } from "../../../../services/platform";
import { useUpdateRequirementDefinition } from "../../../../query/requirementDefinitions/mutation/useUpdateRequirementDefinition";
import { getEditRequirementDefinitionValidationSchema } from "./validationSchema";
import { FormDefaultDataLoader } from "./FormDefaultDataLoader";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { EditBackgroundChecksRequirementDefinitionPartialForm } from "../../modules/BackgroundChecks/editRequirementDefinition/EditBackgroundChecksRequirementDefinitionPartialForm";
import { EditSharedFileRequirementDefinitionPartialForm } from "../../modules/SharedFile/editRequirementDefinition/EditSharedFileRequirementDefinitionPartialForm";
import { EditSignatureRequirementDefinitionPartialForm } from "../../modules/Signature/editRequirementDefinition/EditSignatureRequirementDefinitionPartialForm";
import { EditIdentityVerificationRequirementDefinitionPartialForm } from "../../modules/IdentityVerification/editRequirementDefinition/EditIdentityVerificationRequirementDefinitionPartialForm";

interface RenameRequirementModalProps {
  requirementDefinitionId: string;
  onClose: (result: boolean) => void;
}

export const EditRequirementDefinitionModal: React.FC<
  RenameRequirementModalProps
> = ({ requirementDefinitionId, onClose }) => {
  const { openSnackbar } = useWSSnackbar();
  const queryRequirement = useRequirementDefinitionQuery(
    requirementDefinitionId
  );

  const [updateRequirement, updateRequirementMeta] =
    useUpdateRequirementDefinition(requirementDefinitionId);

  return (
    <WSQueries queries={{ queryRequirement }}>
      {({ queryRequirementData }) => {
        const PartialForm: Record<RequirementType, React.FC> = {
          [RequirementType.BackgroundCheck]:
            EditBackgroundChecksRequirementDefinitionPartialForm,
          [RequirementType.SharedFile]:
            EditSharedFileRequirementDefinitionPartialForm,
          [RequirementType.Signature]:
            EditSignatureRequirementDefinitionPartialForm,
          [RequirementType.IdentityVerification]:
            EditIdentityVerificationRequirementDefinitionPartialForm,
          [RequirementType.InsuranceCoverage]:
            EditIdentityVerificationRequirementDefinitionPartialForm,
          [RequirementType.Registration]: () => null,
          [RequirementType.TaxVerification]: () => null,
          [RequirementType.PayoutMethod]: () => null
        };

        const PartialFormComponent =
          PartialForm[queryRequirementData.requirementType];

        return (
          <FormDefaultDataLoader
            requirementType={queryRequirementData.requirementType}
            dataSourceId={queryRequirementData.dataSourceId}
          >
            {({ loading, error, data, editError, onSubmit }) => {
              if (loading) {
                return <WSLoader.Spinner />;
              }

              if (error) {
                return (
                  <WSErrorMessage
                    contextKey={ErrorContextKey.Other}
                    error={error}
                  />
                );
              }

              return (
                <WSForm
                  defaultValues={{
                    name: queryRequirementData.name,
                    description: queryRequirementData.description,
                    ...data
                  }}
                  validationSchema={getEditRequirementDefinitionValidationSchema(
                    queryRequirementData.requirementType
                  )}
                  onSubmit={async formData => {
                    await updateRequirement(formData, {
                      onSuccess: async () => {
                        // await onSubmit(formData);
                        onClose(true);
                        openSnackbar({
                          message: "Requirement updated successfully",
                          type: "success"
                        });
                      }
                    });
                  }}
                >
                  <WSList gap="L">
                    <WSForm.Field
                      name="name"
                      label="Name"
                      placeholder="Requirement name"
                      component={WSInputText}
                      componentProps={{
                        required: true,
                        helperText:
                          "The requirement name is visible to you and contractors"
                      }}
                    />

                    <WSForm.Field
                      name="description"
                      label="Description (optional)"
                      placeholder="Add optional description for this requirement"
                      component={WSInputTextarea}
                      componentProps={{
                        required: false,
                        helperText:
                          "The description is visible internally to you and your team only"
                      }}
                    />
                    {/* <PartialFormComponent /> */}
                    <WSErrorMessage
                      my="XL"
                      contextKey={ErrorContextKey.UpdateRequirement}
                      error={editError || updateRequirementMeta.error}
                    />
                    <WSActions
                      mt="3XL"
                      alignment="fill"
                      buttons={[
                        {
                          label: "Save changes",
                          kind: "Primary",
                          type: "submit",
                          loading: updateRequirementMeta.isLoading
                        },
                        {
                          label: "Cancel",
                          kind: "Secondary",
                          type: "button",
                          onClick() {
                            onClose(false);
                          }
                        }
                      ]}
                    />
                  </WSList>
                </WSForm>
              );
            }}
          </FormDefaultDataLoader>
        );
      }}
    </WSQueries>
  );
};
